import React from 'react';
import TrophySvg from './TrophySvg';
import AddGuestForm from './AddGuestForm';
import { isNonMemberGuestMembershipType } from '../utils/membership';

const isGuest = u => !!u && (isNonMemberGuestMembershipType(u.membershipType)
  || String(u.playerType || '').trim().toLowerCase() === 'non-member guest');

function TeamProfileContent({ addPlayerToClubTeam, clubs, comps, deleteClubTeam, editingTeamProfile, isAdmin, matches, openComp, openProfile, removePlayerFromClubTeam, saveTeamProfile, setEditingTeamProfile, setTeamProfile, setTeamProfileForm, setUsers, showToast, teamProfile, teamProfileForm, togglePlayerActive, users }) {
  /* Before the early return: a hook cannot sit behind a condition. */
  const [addingGuest, setAddingGuest] = React.useState(false);
  if (!teamProfile) return null;

  /* Straight into the team: a guest is added here because the team is a
     player short, not to be found in the list afterwards. */
  const guestAdded = async (created) => {
    setUsers(prev => [...prev, created]);
    setAddingGuest(false);
    /* If the team refuses them, the guest still exists and the failure has
       already been said — they can be picked from the list once there is room. */
    if (await addPlayerToClubTeam(created.id)) {
      showToast && showToast(`${created.name} added to ${teamProfile.name} as a guest`);
    }
  };
  const userById = new Map(users.map(u => [u.id, u]));
  return (<>
    {/* Team info card */}
    <div className="w3-card w3-round w3-white">
      <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
        <strong><i className="fa fa-shield w3-margin-right"></i>Team Details</strong>
        {teamProfile.id && !editingTeamProfile && (
          <button className="w3-button w3-theme-l2 w3-round w3-right w3-small" onClick={() => setEditingTeamProfile(true)}>
            <i className="fa fa-pencil w3-margin-right"></i>Edit
          </button>
        )}
      </div>
      <div className="w3-container w3-padding">
        {editingTeamProfile ? (
          <div>
            <label className="w3-small w3-text-grey">Team Name *</label>
            <input className="w3-input w3-border w3-round w3-margin-bottom" autoFocus
              value={teamProfileForm.name} onChange={e => setTeamProfileForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && saveTeamProfile()} />
            <label className="w3-small w3-text-grey">Discipline *</label>
            <select className="w3-select w3-border w3-round w3-margin-bottom"
              value={teamProfileForm.discipline} onChange={e => setTeamProfileForm(f => ({ ...f, discipline: e.target.value }))}>
              <option value="pairs">Pairs (2 active)</option>
              <option value="triples">Triples (3 active)</option>
              <option value="fours">Fours (4 active)</option>
            </select>
            {clubs.length > 0 && (
              <>
                <label className="w3-small w3-text-grey">Club</label>
                <select className="w3-select w3-border w3-round w3-margin-bottom"
                  value={teamProfileForm.ClubId || ''}
                  onChange={e => setTeamProfileForm(f => ({ ...f, ClubId: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">— No Club —</option>
                  {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="w3-button w3-theme w3-round" onClick={saveTeamProfile}>
                <i className="fa fa-check w3-margin-right"></i>{teamProfile.id ? 'Save Changes' : 'Create Team'}
              </button>
              <button className="w3-button w3-light-grey w3-round"
                onClick={() => { if (!teamProfile.id) { setTeamProfile(null); } else { setEditingTeamProfile(false); setTeamProfileForm({ name: teamProfile.name, discipline: teamProfile.discipline || 'pairs', ClubId: teamProfile.ClubId || '' }); } }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="profile-avatar-lg" style={{ background: '#607d8b', margin: '0 auto' }}><i className="fa fa-shield"></i></div>
            <h3 style={{ textAlign: 'center', margin: '8px 0 4px' }}>{teamProfile.name}</h3>
            <p className="w3-center" style={{ margin: '4px 0' }}>
              <span className="w3-tag w3-round w3-theme-l3" style={{ textTransform: 'capitalize', fontSize: 'var(--gs-text-sm)' }}>{teamProfile.discipline || 'pairs'}</span>
            </p>
            {teamProfile.Club && (
              <p className="w3-center w3-text-grey w3-small" style={{ margin: '4px 0' }}><i className="fa fa-building w3-margin-right"></i>{teamProfile.Club.name}</p>
            )}
            {teamProfile.players && teamProfile.players.length > 0 && (
              <p className="w3-center w3-text-grey w3-small">{teamProfile.players.length} member{teamProfile.players.length !== 1 ? 's' : ''} &bull; {teamProfile.players.filter(p => p.active).length}/{teamProfile.maxActive || { pairs: 2, triples: 3, fours: 4 }[teamProfile.discipline] || 2} active</p>
            )}
          </div>
        )}
      </div>
    </div>
    {/* Members */}
    {teamProfile.id && (
      <div className="w3-card w3-round w3-white w3-margin-top">
        <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
          <strong><i className="fa fa-users w3-margin-right"></i>Members ({teamProfile.players ? teamProfile.players.length : 0})</strong>
          {teamProfile.players && teamProfile.players.length > 0 && (
            <span className="w3-small w3-text-grey" style={{ marginLeft: '8px' }}>{teamProfile.players.filter(p => p.active).length}/{teamProfile.maxActive || { pairs: 2, triples: 3, fours: 4 }[teamProfile.discipline] || 2} Active</span>
          )}
        </div>
        <div className="w3-container w3-padding">
          {teamProfile.players && teamProfile.players.length > 0 ? (
            <div className="player-grid" style={{ marginBottom: '12px' }}>
              {teamProfile.players.map(p => (
                <div key={p.id} className="player-row w3-hover-light-grey">
                  <div className="player-avatar" style={{ background: p.active ? '#4caf50' : '#bbb' }}><i className="fa fa-user"></i></div>
                  <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openProfile(p.id)}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>
                      {p.name}
                      {isGuest(userById.get(p.id) || p) && (
                        <span className="w3-tag w3-round w3-theme-l4" style={{ marginLeft: '6px', fontSize: 'var(--gs-text-xs)', padding: '1px 6px', fontWeight: 400 }}>Guest</span>
                      )}
                      {p.active
                        ? <span className="w3-tag w3-green w3-round w3-small" style={{ marginLeft: '6px', fontSize: 'var(--gs-text-xs)', padding: '1px 6px' }}>Active</span>
                        : <span className="w3-tag w3-grey w3-round w3-small" style={{ marginLeft: '6px', fontSize: 'var(--gs-text-xs)', padding: '1px 6px' }}>Reserve</span>}
                    </div>
                  </div>
                  <button className={`w3-button w3-tiny w3-round ${p.active ? 'w3-pale-green w3-text-green' : 'w3-light-grey w3-text-grey'}`}
                    onClick={() => togglePlayerActive(p.id)} title={p.active ? 'Deactivate' : 'Activate'} style={{ padding: '2px 8px', marginRight: '4px' }}>
                    <i className={`fa ${p.active ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                  </button>
                  <button className="w3-button w3-tiny w3-text-grey w3-hover-red w3-round"
                    onClick={() => removePlayerFromClubTeam(p.id)} title="Remove" style={{ padding: '2px 8px' }}>
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          ) : (<p className="w3-text-grey w3-center"><em>No members yet</em></p>)}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select className="w3-select w3-border w3-round" style={{ flex: 1 }}
              value="" onChange={e => { if (e.target.value) addPlayerToClubTeam(parseInt(e.target.value)); }}>
              <option value="">+ Add player…</option>
              {users.filter(u => !teamProfile.players.find(p => p.id === u.id)).map(u => (
                <option key={u.id} value={u.id}>{u.name}{isGuest(u) ? ' (guest)' : ''}</option>
              ))}
            </select>
            {isAdmin && (
              <button type="button" className="w3-button w3-round w3-theme-l4" aria-expanded={addingGuest}
                onClick={() => setAddingGuest(v => !v)} style={{ whiteSpace: 'nowrap' }}>
                <i className={`fa ${addingGuest ? 'fa-times' : 'fa-user-plus'} w3-margin-right`}></i>
                {addingGuest ? 'Cancel' : 'Add guest'}
              </button>
            )}
          </div>
          {addingGuest && (
            <div style={{ marginTop: '12px' }}>
              <AddGuestForm users={users} clubId={teamProfile.ClubId || undefined}
                submitLabel="Add guest to team" onAdded={guestAdded} />
            </div>
          )}
        </div>
      </div>
    )}
    {/* Team Record */}
    {teamProfile.id && (<>
      <div className="w3-card w3-round w3-white w3-margin-top">
        <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
          <strong><i className="fa fa-bar-chart w3-margin-right"></i>Team Record</strong>
        </div>
        <div className="w3-container w3-padding">
          <div className="stat-grid">
            <div className="stat-box"><div className="stat-number w3-text-theme">{teamProfile.totals.played}</div><div className="stat-label">Played</div></div>
            <div className="stat-box"><div className="stat-number w3-text-green">{teamProfile.totals.wins}</div><div className="stat-label">Wins</div></div>
            <div className="stat-box"><div className="stat-number w3-text-red">{teamProfile.totals.losses}</div><div className="stat-label">Losses</div></div>
            <div className="stat-box"><div className="stat-number w3-text-amber">{teamProfile.totals.draws}</div><div className="stat-label">Draws</div></div>
            <div className="stat-box"><div className="stat-number w3-text-theme">{teamProfile.totals.pointsFor}</div><div className="stat-label">Points For</div></div>
            <div className="stat-box"><div className="stat-number" style={{ color: '#888' }}>{teamProfile.totals.pointsAgainst}</div><div className="stat-label">Points Against</div></div>
            <div className="stat-box">
              <div className={`stat-number ${(teamProfile.totals.pointsFor - teamProfile.totals.pointsAgainst) >= 0 ? 'w3-text-green' : 'w3-text-red'}`}>
                {(teamProfile.totals.pointsFor - teamProfile.totals.pointsAgainst) > 0 ? '+' : ''}{teamProfile.totals.pointsFor - teamProfile.totals.pointsAgainst}
              </div><div className="stat-label">Diff</div>
            </div>
            <div className="stat-box">
              <div className="stat-number w3-text-theme">{teamProfile.totals.played > 0 ? Math.round((teamProfile.totals.wins / teamProfile.totals.played) * 100) : 0}%</div>
              <div className="stat-label">Win Rate</div>
            </div>
          </div>
        </div>
      </div>
      {/* Competition History */}
      <div className="w3-card w3-round w3-white w3-margin-top">
        <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
          <strong><TrophySvg size={14} style={{ marginRight: '8px' }} />Competition History ({teamProfile.competitions.length})</strong>
        </div>
        <div className="w3-container w3-padding">
          {teamProfile.competitions.length === 0 ? (
            <p className="w3-text-grey w3-center"><em>Not yet entered in any competitions</em></p>
          ) : (
            <div className="w3-responsive">
              <table className="w3-table w3-striped w3-bordered w3-small">
                <thead className="w3-theme-d5"><tr><th>Competition</th><th>Format</th><th>Status</th><th>Played</th><th>Won</th><th>Lost</th><th>Drawn</th><th>Points For</th><th>Against</th><th>Difference</th></tr></thead>
                <tbody>
                  {teamProfile.competitions.map(c => {
                    const stats = teamProfile.competitionStats.find(s => s.competitionId === c.id);
                    const fmtLabel2 = { singles: 'Singles', pairs: 'Pairs', triples: 'Triples', fours: 'Fours', '2-4-2-pairs': '2-4-2', custom: 'Custom' }[c.format] || c.format;
                    return (
                      <tr key={c.id} className="w3-hover-light-grey" style={{ cursor: 'pointer' }}
                        onClick={() => { const comp = comps.find(x => x.id === c.id); if (comp) openComp(comp); }}>
                        <td><strong>{c.name}</strong></td>
                        <td><span className="w3-tag w3-round w3-theme-l3 w3-small">{fmtLabel2}</span></td>
                        <td><span className={`w3-tag w3-round w3-small ${c.status === 'in-progress' ? 'w3-green' : c.status === 'completed' ? 'w3-blue' : 'w3-grey'}`}>{c.status || 'draft'}</span></td>
                        {stats ? (<><td>{stats.played}</td><td className="w3-text-green">{stats.wins}</td><td className="w3-text-red">{stats.losses}</td><td>{stats.draws}</td><td>{stats.pointsFor}</td><td>{stats.pointsAgainst}</td>
                          <td className={stats.pointsFor - stats.pointsAgainst >= 0 ? 'w3-text-green' : 'w3-text-red'}>{stats.pointsFor - stats.pointsAgainst > 0 ? '+' : ''}{stats.pointsFor - stats.pointsAgainst}</td>
                        </>) : (<td colSpan="7" className="w3-text-grey"><em>No matches yet</em></td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Danger Zone */}
      {isAdmin && <div className="w3-card w3-round w3-white w3-margin-top w3-margin-bottom">
        <div className="w3-container w3-pale-red w3-round-top" style={{ padding: '12px 16px' }}>
          <strong><i className="fa fa-exclamation-triangle w3-margin-right"></i>Danger Zone</strong>
        </div>
        <div className="w3-container w3-padding">
          <p className="w3-small w3-text-grey">Permanently delete this team. Players will not be affected. This action cannot be undone.</p>
          <button className="w3-button w3-red w3-round" onClick={deleteClubTeam}><i className="fa fa-trash w3-margin-right"></i>Delete Team</button>
        </div>
      </div>}
    </>)}
  </>);
}

export default TeamProfileContent;
